import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';

// URL Base API Backend
const API_BASE = 'https://sijapan.sman10fajarharapan.sch.id/api';

const JadwalManager = ({ token }) => {
  const [jadwalList, setJadwalList] = useState([]);
  const [guruList, setGuruList] = useState([]);
  const [kelasList, setKelasList] = useState([]);
  const [mapelList, setMapelList] = useState([]); 
  const [loading, setLoading] = useState(false);

  // State untuk Checkbox (Pilih Banyak)
  const [selectedIds, setSelectedIds] = useState([]);

  // Form State
  const [hari, setHari] = useState('Senin');
  const [jamMulai, setJamMulai] = useState('');
  const [jamSelesai, setJamSelesai] = useState('');
  const [mapel, setMapel] = useState('');
  const [guruId, setGuruId] = useState('');
  const [kelasId, setKelasId] = useState('');

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [resJadwal, resGuru, resKelas, resMapel] = await Promise.all([
        axios.get(`${API_BASE}/jadwal`, { headers }),
        axios.get(`${API_BASE}/guru`, { headers }),
        axios.get(`${API_BASE}/kelas`, { headers }),
        axios.get(`${API_BASE}/mapel`, { headers })
      ]);

      setJadwalList(resJadwal.data);
      setGuruList(resGuru.data);
      setKelasList(resKelas.data);
      setMapelList(resMapel.data);
      setSelectedIds([]); // reset centangan setiap load data
    } catch (err) {
      console.error("Error Fetch Data:", err);
    }
  };

  // --- LOGIKA CHECKBOX ---
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(jadwalList.map(item => item.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(item => item !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  // --- TOGGLE SINGLE (SEMBUNYIKAN / TAMPILKAN) ---
  const handleToggleSingle = async (id, currentStatus) => {
    const newStatus = currentStatus === 1 ? 0 : 1;
    const actionText = newStatus === 0 ? 'menyembunyikan' : 'mengaktifkan';
    
    try {
      setLoading(true);
      await axios.put(`${API_BASE}/jadwal/${id}/toggle-status`, { is_active: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (err) {
      Swal.fire('Gagal', `Gagal ${actionText} jadwal.`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // --- TOGGLE MASSAL (SEMBUNYIKAN / AKTIFKAN BANYAK JADWAL) ---
  const handleToggleMassal = async (targetStatus) => {
    if (selectedIds.length === 0) return;
    const label = targetStatus === 0 ? 'Sembunyikan' : 'Aktifkan';

    const result = await Swal.fire({
      title: `${label} ${selectedIds.length} Jadwal Terpilih?`,
      text: targetStatus === 0 
        ? "Jadwal akan disembunyikan dari guru piket & KBM, tetapi histori absen tetap aman."
        : "Jadwal akan dimunculkan kembali di sistem.",
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: targetStatus === 0 ? '#f59e0b' : '#10b981',
      cancelButtonColor: '#64748b',
      confirmButtonText: `Ya, ${label}!`,
      cancelButtonText: 'Batal'
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        await axios.put(`${API_BASE}/jadwal/toggle-massal`, {
          ids: selectedIds,
          is_active: targetStatus
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        Swal.fire('Berhasil', `Berhasil ${label.toLowerCase()} ${selectedIds.length} jadwal!`, 'success');
        fetchData();
      } catch (err) {
        Swal.fire('Gagal', `Gagal mengubah status massal.`, 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  // --- HAPUS MASSAL YANG DICENTANG ---
  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return;

    const result = await Swal.fire({
      title: `Hapus Permanen ${selectedIds.length} Jadwal?`,
      text: "PERINGATAN: Menghapus jadwal akan menghapus relasi data. Jika hanya ingin mengganti semester, gunakan tombol 'Sembunyikan'!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Ya, Hapus Permanen!',
      cancelButtonText: 'Batal'
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        await Promise.all(
          selectedIds.map(id =>
            axios.delete(`${API_BASE}/jadwal/${id}`, {
              headers: { Authorization: `Bearer ${token}` }
            })
          )
        );
        Swal.fire('Berhasil', `${selectedIds.length} jadwal berhasil dihapus!`, 'success');
        fetchData();
      } catch (err) {
        Swal.fire('Gagal', 'Beberapa atau semua jadwal gagal dihapus.', 'error');
      }
      setLoading(false);
    }
  };

  // --- HAPUS SEMUA (TOTAL RESET) ---
  const handleResetAll = async () => {
    const result = await Swal.fire({
      title: 'Hapus SEMUA Jadwal?',
      text: "Seluruh jadwal pelajaran di aplikasi akan dikosongkan total!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d97706',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Ya, Kosongkan Semua!',
      cancelButtonText: 'Batal'
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        await axios.delete(`${API_BASE}/jadwal/reset`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        Swal.fire('Berhasil', 'Semua jadwal berhasil dikosongkan!', 'success');
        fetchData();
      } catch (err) {
        Swal.fire('Gagal', 'Gagal menghapus semua jadwal.', 'error');
      }
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_BASE}/jadwal`, {
        hari, jam_mulai: jamMulai, jam_selesai: jamSelesai, mapel, guru_id: guruId, kelas_id: kelasId
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      Swal.fire('Berhasil', 'Jadwal berhasil disimpan!', 'success');
      fetchData(); 
      setMapel(''); 
      setGuruId('');
    } catch (err) {
      Swal.fire('Gagal', 'Gagal menyimpan jadwal.', 'error');
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if(!window.confirm('Yakin hapus permanen jadwal ini?')) return;
    try {
      await axios.delete(`${API_BASE}/jadwal/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchData();
    } catch (err) {
      alert('Gagal menghapus.');
    }
  };

  // --- FITUR IMPORT EXCEL ---
  const handleImport = async () => {
    const { value: file } = await Swal.fire({
        title: 'Import File Excel Jadwal',
        html: `<p style="font-size:13px">Pastikan nama GURU dan KELAS di Excel <b>sama persis</b> dengan di aplikasi.</p>
               <p style="font-size:12px; color:#64748b; background:#f1f5f9; padding:10px; border-radius:6px">
                 <b>Kolom Wajib:</b><br/>
                 HARI, JAM_MULAI, JAM_SELESAI, MAPEL, NAMA_GURU, NAMA_KELAS
               </p>`,
        input: 'file',
        inputAttributes: {
          accept: '.xlsx, .xls'
        },
        showCancelButton: true,
        confirmButtonText: 'Upload File',
        confirmButtonColor: '#10b981'
    });

    if (file) {
        const formData = new FormData();
        formData.append('file', file);
        try {
            setLoading(true);
            const res = await axios.post(`${API_BASE}/import-jadwal`, formData, {
                headers: { 
                  Authorization: `Bearer ${token}`, 
                  'Content-Type': 'multipart/form-data' 
                }
            });
            
            if (res.data.errors && res.data.errors.length > 0) {
              Swal.fire({
                title: 'Import Selesai Sebagian',
                html: `<p>Berhasil: <b>${res.data.sukses}</b> dari <b>${res.data.total}</b> jadwal.</p>
                       <div style="text-align:left; font-size:12px; color:#ef4444; max-height:150px; overflow-y:auto; background:#fef2f2; padding:10px; border-radius:6px">
                         ${res.data.errors.map(err => `• ${err}`).join('<br/>')}
                       </div>`,
                icon: 'warning'
              });
            } else {
              Swal.fire('Sukses', `Berhasil import: ${res.data.sukses} jadwal.`, 'success');
            }
            
            fetchData();
        } catch (err) {
            console.error("Import Error Detail:", err);
            const errorMsg = err.response?.data?.message || err.message || 'Terjadi kesalahan jaringan/CORS.';
            Swal.fire('Gagal Import File', errorMsg, 'error');
        }
        setLoading(false);
    }
  };

  // --- FITUR PASTE JADWAL DARI EXCEL ---
  const handlePasteImport = async () => {
    const { value: text } = await Swal.fire({
      title: '📋 Copy-Paste Jadwal dari Excel',
      html: `
        <div style="text-align: left; font-size: 12px; color: #475569; margin-bottom: 8px;">
          <b>Urutan Kolom Wajib di Excel:</b><br/>
          <code style="background: #e2e8f0; padding: 2px 6px; border-radius: 4px;">HARI | JAM_MULAI | JAM_SELESAI | MAPEL | NAMA_GURU | NAMA_KELAS</code>
        </div>
      `,
      input: 'textarea',
      inputPlaceholder: '1. Buka File Excel Jadwal\n2. Blok seluruh tabel jadwal\n3. Tekan Ctrl+C\n4. Paste (Ctrl+V) di box ini...',
      inputAttributes: {
        'aria-label': 'Paste jadwal di sini',
        style: 'height: 180px; font-family: monospace; font-size: 12px;'
      },
      showCancelButton: true,
      confirmButtonText: '⚡ Proses Data',
      confirmButtonColor: '#4361ee',
      cancelButtonText: 'Batal'
    });

    if (text) {
      try {
        setLoading(true);
        const res = await axios.post(`${API_BASE}/paste-jadwal`, { rawText: text }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (res.data.errors && res.data.errors.length > 0) {
          Swal.fire({
            title: 'Proses Selesai Sebagian',
            html: `<p>Berhasil: <b>${res.data.sukses}</b> baris.</p>
                   <div style="text-align:left; font-size:12px; color:#ef4444; max-height:150px; overflow-y:auto; background:#fef2f2; padding:10px; border-radius:6px">
                     ${res.data.errors.map(err => `• ${err}`).join('<br/>')}
                   </div>`,
            icon: 'warning'
          });
        } else {
          Swal.fire('Berhasil!', `Sukses mengimport ${res.data.sukses} jadwal pelajaran.`, 'success');
        }
        fetchData();
      } catch (err) {
        Swal.fire('Gagal', err.response?.data?.message || 'Terjadi kesalahan saat memproses data.', 'error');
      }
      setLoading(false);
    }
  };

  const days = ['Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];

  return (
    <div style={{ paddingBottom: '50px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: '25px', flexWrap:'wrap', gap:'10px' }}>
        <div>
            <h2 style={{ margin: 0, color: '#1e293b' }}>📅 Atur Jadwal Pelajaran</h2>
            <p style={{ margin: '5px 0 0', color: '#64748b', fontSize: '14px' }}>Susun jadwal aktif atau sembunyikan jadwal semester lama.</p>
        </div>
        
        {/* ACTION BUTTONS */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          
          {/* AKSI BANYAK (JIKA ADA YANG DICENTANG) */}
          {selectedIds.length > 0 && (
            <>
              <button 
                onClick={() => handleToggleMassal(0)} 
                disabled={loading}
                style={{ padding: '10px 14px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight:'bold', fontSize:'13px' }}
              >
                👁️ Sembunyikan ({selectedIds.length})
              </button>
              <button 
                onClick={() => handleToggleMassal(1)} 
                disabled={loading}
                style={{ padding: '10px 14px', background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight:'bold', fontSize:'13px' }}
              >
                ✅ Aktifkan ({selectedIds.length})
              </button>
              <button 
                onClick={handleDeleteSelected} 
                disabled={loading}
                style={{ padding: '10px 14px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight:'bold', fontSize:'13px' }}
              >
                🗑️ Hapus Terpilih ({selectedIds.length})
              </button>
            </>
          )}

          {/* TOMBOL HAPUS TOTAL */}
          <button 
            onClick={handleResetAll} 
            disabled={loading || jadwalList.length === 0}
            style={{ 
              padding: '10px 14px', 
              background: jadwalList.length === 0 ? '#cbd5e1' : '#64748b', 
              color: 'white', 
              border: 'none', 
              borderRadius: '8px', 
              cursor: jadwalList.length === 0 ? 'not-allowed' : 'pointer', 
              fontWeight:'bold',
              fontSize:'13px'
            }}
          >
            ⚠️ Reset Semua
          </button>

          {/* TOMBOL PASTE EXCEL */}
          <button 
            onClick={handlePasteImport} 
            disabled={loading}
            style={{ padding: '10px 14px', background: '#4361ee', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight:'bold', fontSize:'13px' }}
          >
            📋 Paste Excel
          </button>

          {/* TOMBOL UPLOAD FILE EXCEL */}
          <button 
            onClick={handleImport} 
            disabled={loading}
            style={{ padding: '10px 14px', background: '#059669', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight:'bold', fontSize:'13px' }}
          >
            📥 File Excel
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
        
        {/* FORM INPUT */}
        <div style={{ flex: 1, minWidth: '300px' }}>
          <div style={{ background: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
            <h3 style={{ marginTop: 0, fontSize: '16px', color: '#334155' }}>Buat Jadwal Baru</h3>
            <form onSubmit={handleSubmit}>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', fontSize: '12px', marginBottom: '5px', color: '#64748b' }}>Hari</label>
                  <select value={hari} onChange={e => setHari(e.target.value)} style={{width:'100%', padding:'10px'}}>
                    {days.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                   <label style={{ display: 'block', fontWeight: '600', fontSize: '12px', marginBottom: '5px', color: '#64748b' }}>Kelas</label>
                   <select value={kelasId} onChange={e => setKelasId(e.target.value)} required style={{width:'100%', padding:'10px'}}>
                    <option value="">-- Pilih Kelas --</option>
                    {kelasList.map(k => <option key={k.id} value={k.id}>{k.nama_kelas}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', fontSize: '12px', marginBottom: '5px', color: '#64748b' }}>Jam Mulai</label>
                  <input type="time" value={jamMulai} onChange={e => setJamMulai(e.target.value)} required style={{width:'100%', padding:'10px'}} />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', fontSize: '12px', marginBottom: '5px', color: '#64748b' }}>Jam Selesai</label>
                  <input type="time" value={jamSelesai} onChange={e => setJamSelesai(e.target.value)} required style={{width:'100%', padding:'10px'}} />
                </div>
              </div>

              {/* DROPDOWN MAPEL */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', fontWeight: '600', fontSize: '12px', marginBottom: '5px', color: '#64748b' }}>Mata Pelajaran</label>
                <select value={mapel} onChange={e => setMapel(e.target.value)} required style={{width:'100%', padding:'10px'}}>
                  <option value="">-- Pilih Mapel --</option>
                  {mapelList.map(m => (
                    <option key={m.id} value={m.nama_mapel}>{m.nama_mapel}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontWeight: '600', fontSize: '12px', marginBottom: '5px', color: '#64748b' }}>Guru Pengajar</label>
                <select value={guruId} onChange={e => setGuruId(e.target.value)} required style={{width:'100%', padding:'10px'}}>
                  <option value="">-- Pilih Guru --</option>
                  {guruList.map(g => <option key={g.id} value={g.id}>{g.nama} ({g.mapel})</option>)}
                </select>
              </div>

              <button type="submit" disabled={loading} style={{ width: '100%', padding: '12px', background: '#4361ee', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', cursor:'pointer' }}>
                {loading ? 'Menyimpan...' : '➕ Simpan Jadwal'}
              </button>
            </form>
          </div>
        </div>

        {/* TABEL LIST */}
        <div style={{ flex: 2, minWidth: '300px' }}>
          <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
            <table style={{ margin: 0, width:'100%', borderCollapse: 'collapse' }}>
              <thead style={{ background:'#f8fafc', textAlign:'left' }}>
                <tr>
                  <th style={{ padding:'12px', width: '40px', textAlign: 'center' }}>
                    <input 
                      type="checkbox" 
                      onChange={handleSelectAll} 
                      checked={jadwalList.length > 0 && selectedIds.length === jadwalList.length}
                    />
                  </th>
                  <th style={{padding:'12px'}}>Hari</th>
                  <th style={{padding:'12px'}}>Jam</th>
                  <th style={{padding:'12px'}}>Kelas</th>
                  <th style={{padding:'12px'}}>Mapel</th>
                  <th style={{padding:'12px'}}>Guru</th>
                  <th style={{padding:'12px', textAlign:'center'}}>Status</th>
                  <th style={{ textAlign: 'center', padding:'12px' }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {jadwalList.map((item) => {
                  const isActive = item.is_active === undefined || item.is_active === 1;
                  return (
                    <tr 
                      key={item.id} 
                      style={{
                        borderBottom:'1px solid #f1f5f9', 
                        background: selectedIds.includes(item.id) ? '#fef2f2' : (!isActive ? '#f8fafc' : 'transparent'),
                        opacity: isActive ? 1 : 0.65
                      }}
                    >
                      <td style={{ textAlign: 'center', padding:'12px' }}>
                        <input 
                          type="checkbox" 
                          checked={selectedIds.includes(item.id)} 
                          onChange={() => handleSelectOne(item.id)}
                        />
                      </td>
                      <td style={{ fontWeight: '700', color: isActive ? '#4361ee' : '#94a3b8', padding:'12px' }}>{item.hari}</td>
                      <td style={{padding:'12px'}}>{item.jam_mulai ? item.jam_mulai.slice(0,5) : ''} - {item.jam_selesai ? item.jam_selesai.slice(0,5) : ''}</td>
                      <td style={{padding:'12px'}}>
                        <span style={{background: isActive ? '#e0e7ff' : '#e2e8f0', color: isActive ? '#4361ee' : '#64748b', padding: '4px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '700'}}>
                          {item.nama_kelas}
                        </span>
                      </td>
                      <td style={{ fontWeight: '600', color: isActive ? '#0f172a' : '#64748b', padding:'12px' }}>{item.mapel}</td>
                      <td style={{padding:'12px', color: isActive ? '#334155' : '#64748b'}}>{item.nama_guru}</td>
                      
                      {/* STATUS BADGE */}
                      <td style={{ textAlign: 'center', padding:'12px' }}>
                        <span style={{
                          fontSize: '11px',
                          padding: '3px 8px',
                          borderRadius: '12px',
                          fontWeight: 'bold',
                          background: isActive ? '#dcfce7' : '#fef3c7',
                          color: isActive ? '#15803d' : '#b45309'
                        }}>
                          {isActive ? 'Aktif' : 'Sembunyi'}
                        </span>
                      </td>

                      {/* AKSI BUTTONS */}
                      <td style={{ textAlign: 'center', padding:'12px', whiteSpace: 'nowrap' }}>
                        <button 
                          onClick={() => handleToggleSingle(item.id, item.is_active ?? 1)}
                          title={isActive ? "Sembunyikan dari jadwal aktif" : "Tampilkan kembali"}
                          style={{ 
                            background: isActive ? '#fef3c7' : '#dcfce7', 
                            color: isActive ? '#d97706' : '#16a34a', 
                            border: 'none', 
                            padding: '6px 10px', 
                            borderRadius: '6px', 
                            fontSize: '11px', 
                            fontWeight: '700', 
                            cursor:'pointer',
                            marginRight: '6px'
                          }}
                        >
                          {isActive ? '👁️ Sembunyi' : '✅ Aktifkan'}
                        </button>
                        <button 
                          onClick={() => handleDelete(item.id)}
                          style={{ background: '#fee2e2', color: '#ef4444', border: 'none', padding: '6px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '700', cursor:'pointer' }}
                        >
                          Hapus
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {jadwalList.length === 0 && (
                  <tr><td colSpan="8" style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>Belum ada jadwal.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
};

export default JadwalManager;